/**
 * useSiteContent — shared hook that fetches `/site-content` ONCE per page-load
 * and caches the result in localStorage so the first render after a refresh
 * uses the last-known values instantly (no flash of old/default text).
 *
 * PHASE 8.44 — Boot pre-fetch:
 *   `public/index.html` now fires `/api/site-content` BEFORE the React bundle
 *   finishes downloading. By the time this hook mounts, the result is on
 *   `window.__OODEL_BOOT__.site_content`. We seed initial state from there
 *   FIRST (truly fresh — no flash), then fall back to localStorage cache,
 *   then to `{}`. The hook still kicks off its own `/site-content` GET as
 *   a revalidation, but the data is identical so the user never sees a
 *   re-render flash.
 */
import { useEffect, useState } from "react";
import api from "./api";

const CACHE_KEY = "oodel-site-content-v1";

let _cachedPromise = null;          // de-dupe in-flight requests across hook usages

function readBoot() {
  try {
    if (typeof window !== "undefined" && window.__OODEL_BOOT__?.site_content) {
      return window.__OODEL_BOOT__.site_content;
    }
  } catch { /* ignored */ }
  return null;
}

function readCache() {
  // Boot data beats localStorage — it's strictly fresher (sub-second old)
  // and has the admin's latest CMS edits.
  const boot = readBoot();
  if (boot && typeof boot === "object") return boot;
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeCache(data) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch { /* ignored */ }
}

// Synchronous helper — any component that fetches `/site-content` itself
// (instead of going through `useSiteContent`) should seed its useState
// initial value from this so the first paint shows the fresh CMS values
// already written by the boot pre-fetch.
export function getCachedSiteContent() {
  return readCache();
}

export function useSiteContent() {
  const [content, setContent] = useState(readCache);

  useEffect(() => {
    // Cache the boot data into localStorage on first mount so subsequent
    // page loads (where boot pre-fetch hasn't fired yet) still benefit.
    const boot = readBoot();
    if (boot) writeCache(boot);

    let cancelled = false;
    // A single force-refresh helper we can call from mount, from an interval,
    // and from browser lifecycle events (focus / visibility). Bypasses the
    // module-level `_cachedPromise` dedupe so we don't get stuck on a stale
    // resolved promise after the admin flips a CMS toggle.
    const refresh = () => {
      api.get("/site-content")
        .then((r) => r.data || {})
        .catch(() => null)
        .then((data) => {
          if (cancelled || !data) return;
          writeCache(data);
          setContent(data);
        });
    };

    // Initial fetch (de-duped across every mounted useSiteContent consumer
    // via the module-level `_cachedPromise` so the very first paint of a
    // multi-consumer page doesn't fan out into N identical requests).
    if (!_cachedPromise) {
      _cachedPromise = api.get("/site-content")
        .then((r) => r.data || {})
        .catch(() => ({}));
    }
    _cachedPromise.then((data) => {
      if (cancelled) return;
      writeCache(data);
      setContent(data);
      setTimeout(() => { _cachedPromise = null; }, 100);
    });

    // Keep the public site in sync with live admin CMS edits without a
    // hard refresh: poll every 60s AND refetch whenever the tab regains
    // visibility / focus. Cheap for a 193-key JSON payload; kills the
    // "I turned this OFF in admin but the menu is still showing" bug.
    const intervalId = setInterval(refresh, 60_000);
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    const onFocus = () => refresh();
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return content;
}

// Companion hook — returns true once /site-content has resolved (even if
// the response was an empty object). PHASE 8.44 — when boot data is
// present we flip `ready=true` synchronously so consumers that gate
// behind it (e.g. PublicNav) paint correctly on the very first frame.
let _siteContentResolvedOnce = false;
if (typeof window !== "undefined" && window.__OODEL_BOOT__?.site_content) {
  _siteContentResolvedOnce = true;
}
export function useSiteContentReady() {
  const [ready, setReady] = useState(_siteContentResolvedOnce);
  useEffect(() => {
    if (_siteContentResolvedOnce) return;
    const tick = () => {
      if (_cachedPromise) {
        _cachedPromise.then(() => { _siteContentResolvedOnce = true; setReady(true); });
      } else {
        // Sibling already finished — flip on next tick.
        _siteContentResolvedOnce = true;
        setReady(true);
      }
    };
    tick();
  }, []);
  return ready;
}

export default useSiteContent;
